// Live meeting types — snake_case per contracts.md §8.2/§8.3/§8.4/§8.5.

export type TranscriberStatus = 'stopped' | 'running' | 'paused';
export type TranscriptSource = 'feed' | 'paste' | 'mic';

// A unique person who has joined the live session (server-recorded on stream
// connect while live; cumulative across the session).
export interface LiveParticipant {
  user_id: string;
  display_name: string;
  joined_at: string;
}

export interface LiveState {
  is_live: boolean;
  // UTC ISO instant the session went live; null when not live. Drives the
  // elapsed-time display next to the LIVE badge (server-owned so it survives
  // refresh/reconnect and is consistent across viewers).
  started_at?: string | null;
  // UTC ISO instant the most recent session stopped; retained after stop.
  ended_at?: string | null;
  // Duration in seconds of the most recent completed session, recorded on stop
  // and retained so the final elapsed time can be shown once not live.
  last_session_seconds?: number | null;
  active_slide_ref: string | null;
  transcriber_status: TranscriberStatus;
  controller_user_id: string | null;
  // Ordered deck selected by the secretary at go-live (Unit 10a). null/empty
  // means "all meeting materials" (the pre-picker default behaviour).
  presentation_file_ids?: string[] | null;
  // Unique people who have joined the current/most recent live session, in join
  // order. Reset when a new session starts.
  participants?: LiveParticipant[];
}

export interface LiveStateResponse {
  live_state: LiveState;
}

export interface TranscriptSegment {
  id: string;
  meeting_id: string;
  sequence: number;
  speaker?: string | null;
  text: string;
  timestamp: string;
  source?: TranscriptSource;
}

export interface Citation {
  source: string;
  ref: string;
  // True when the source matched an actual material file attached to the
  // meeting at generation time. Absent on older backends; treat as false.
  verified?: boolean;
}

// 'page' = slide-scoped per-page stream (default); 'pack' = meeting-level top
// insights for the whole deck. Absent on older backends; treat as 'page'.
export type InsightCategory = 'page' | 'pack';

export interface LiveInsight {
  id: string;
  meeting_id: string;
  user_id: string;
  slide_ref?: string | null;
  content: string;
  citations: Citation[];
  created_at: string;
  category?: InsightCategory;
}

export type QuestionStatus = 'suggested' | 'asked' | 'raised' | 'addressed';
// 'ask'/'raise'/'lower' are owner actions on the caller's own question; 'address'
// is a presenter (live controller) action marking a raised question handled.
export type QuestionAction = 'ask' | 'raise' | 'lower' | 'address';
// 'grounded' questions are answerable from the meeting materials and can be
// promoted to the Advisor; 'discussion' questions are attendee-facing talking
// points that cannot be asked to the Advisor. Absent on older backends; treat a
// missing kind as 'discussion' (never Advisor-promotable).
export type QuestionKind = 'grounded' | 'discussion';

export interface SuggestedQuestion {
  id: string;
  meeting_id: string;
  user_id: string;
  slide_ref: string;
  text: string;
  status: QuestionStatus;
  question_kind?: QuestionKind;
}

// Presenter-facing payload of the whole-meeting `question.raised` event: a
// participant has raised one of their (normally private) questions to the
// presenter, opting in to reveal their identity + the question text.
export interface RaisedQuestion {
  question_id: string;
  meeting_id: string;
  user_id: string;
  display_name: string;
  role?: string | null;
  text: string;
  question_kind?: QuestionKind;
  raised_at: string;
}

// Payload of `question.addressed` / `question.lowered`: identifies the question
// leaving the presenter's raised-hands queue and its owning attendee.
export interface RaisedQuestionRemoval {
  question_id: string;
  user_id: string;
}

// SSE event channel names carried on the live stream (contracts.md §8.2).
export type LiveEventName =
  | 'live.state'
  | 'slide.changed'
  | 'transcript.segment'
  | 'transcript.cleared'
  | 'insight.updated'
  | 'question.updated'
  | 'question.raised'
  | 'question.addressed'
  | 'question.lowered'
  | 'heartbeat'
  | 'error';

// A parsed live_state ref of the form "{fileId}:{page}".
export interface SlideRef {
  fileId: string;
  page: number;
  raw: string;
}

// Parse a slide_ref string "{fileId}:{page}" into its parts. Returns null when
// the ref is empty or not in the fileId:page form (e.g. an abstract slide id).
export const parseSlideRef = (ref: string | null | undefined): SlideRef | null => {
  if (!ref) return null;
  const idx = ref.lastIndexOf(':');
  if (idx <= 0 || idx === ref.length - 1) return null;
  const fileId = ref.slice(0, idx);
  const page = Number(ref.slice(idx + 1));
  if (!fileId || !Number.isFinite(page) || page < 1) return null;
  return { fileId, page: Math.floor(page), raw: ref };
};

// Build a slide_ref string from a file id and 1-based page number.
export const buildSlideRef = (fileId: string, page: number): string => `${fileId}:${page}`;

// ---- Teams live-transcription feed (teams-live-transcription-plan.md) ----

// Response of POST /meetings/{id}/live/teams-token (Unit T1).
export interface TeamsTokenResponse {
  token: string;
  expires_on: string;
  acs_user_id: string;
  display_name: string;
}

// Response of POST /meetings/{id}/live/speech-token (Azure AI Speech mic path).
export interface SpeechTokenResponse {
  token: string;
  region: string;
  language: string;
}

// Lifecycle of the browser-hosted Teams transcriber agent (Unit T3).
export type TeamsCaptureStatus =
  | 'idle'
  | 'connecting'
  | 'inLobby'
  | 'connected'
  | 'captioning'
  | 'error';

// A finalized Teams caption mapped for segment ingestion (source "feed").
export interface TeamsFinalCaption {
  speaker: string;
  text: string;
  timestamp: string;
}

// A segment ready to POST to the transcript segments API (source "paste").
export interface PastedSegment {
  sequence: number;
  speaker?: string;
  text: string;
  timestamp: string;
}

// Split pasted transcript text into ordered segments for the live segments API.
// Each non-blank line (paragraphs collapse on blank lines) becomes one segment;
// a leading "Speaker: text" prefix is parsed into the speaker field. Sequence
// numbers continue after `afterSequence` so pasted lines append to the live
// transcript rather than colliding with existing segments.
export const splitPasteIntoSegments = (
  text: string,
  afterSequence: number,
  timestamp: string
): PastedSegment[] => {
  const lines = text
    .split(/\r?\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.map((line, i) => {
    const match = line.match(/^([^:]{1,40}):\s+(.+)$/);
    const speaker = match ? match[1].trim() : undefined;
    const body = match ? match[2].trim() : line;
    return {
      sequence: afterSequence + 1 + i,
      ...(speaker ? { speaker } : {}),
      text: body,
      timestamp,
    };
  });
};
